import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import MemeCache from "@/components/MemeCache";
import {
  dismissFinFoxMessage,
  subscribeFinFox,
  type FinFoxMessage,
} from "@/lib/finfoxBus";



import smartHoodie from "@/assets/finfox/smart-hoodie.png";
import happyHoodie from "@/assets/finfox/happy-hoodie.png";
import curiousHoodie from "@/assets/finfox/curious-hoodie.png";
import concernedHoodie from "@/assets/finfox/concerned-hoodie.png";
import celebratingHoodie from "@/assets/finfox/celebrating-hoodie.png";

export type FoxMood = "smart" | "happy" | "curious" | "concerned" | "celebrating";

const MOOD_MAP: Record<FoxMood, string> = {
  smart: smartHoodie,
  happy: happyHoodie,
  curious: curiousHoodie,
  concerned: concernedHoodie,
  celebrating: celebratingHoodie,
};

type Quip = { mood: FoxMood; text: string };

const ROUTE_QUIPS: Record<string, Quip[]> = {
  "/": [
    { mood: "smart", text: "Welcome back. Let's compound a little wisdom today." },
    { mood: "curious", text: "What story is the market telling this morning?" },
    { mood: "happy", text: "Small daily reps beat one big gamble. Always." },
  ],
  "/financial-tinder": [
    { mood: "curious", text: "Swipe with intent — every card is a tiny lesson." },
    { mood: "smart", text: "Read the 'why' before you swipe right." },
  ],
  "/financial-league": [
    { mood: "smart", text: "Thesis first, trade second. Always in that order." },
    { mood: "concerned", text: "If you can't explain the trade in one line, skip it." },
    { mood: "celebrating", text: "Discipline > dopamine. You're playing the long game." },
  ],
  "/community": [
    { mood: "happy", text: "Share your reasoning, not just your returns." },
  ],
  "/dashboard": [
    { mood: "smart", text: "Streaks are stories. What's yours saying?" },
  ],
  "/trading-journal": [
    { mood: "concerned", text: "Tag the emotion, not just the trade." },
  ],
  "/interview-prep": [
    { mood: "smart", text: "Frameworks beat memorized answers." },
  ],
};

const FALLBACK_QUIPS: Quip[] = [
  { mood: "smart", text: "Sharp ears, sharper decisions." },
  { mood: "happy", text: "Consistency compounds. So do you." },
  { mood: "curious", text: "Ask one good question today." },
  { mood: "concerned", text: "Risk first. Returns follow." },
];

function getRouteKey(path: string): string {
  if (path === "/") return "/";
  const keys = Object.keys(ROUTE_QUIPS).filter((k) => k !== "/");
  const hit = keys.find((k) => path.startsWith(k));
  return hit ?? "_";
}

export default function FinFoxMascot() {
  const location = useLocation();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(() => localStorage.getItem("finplay:finfox") !== "off");
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [cacheOpen, setCacheOpen] = useState(false);
  const [messages, setMessages] = useState<FinFoxMessage[]>([]);
  /** Index of the chosen option on a question message, or null before answering. */
  const [answered, setAnswered] = useState<number | null>(null);
  const lastTapRef = useRef(0);

  useEffect(() => subscribeFinFox(setMessages), []);

  // Secret: double-tap FinFox to open the Meme Cache instead of the tip bubble.
  const handleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      lastTapRef.current = 0;
      setOpen(false);
      setCacheOpen(true);
      return;
    }
    lastTapRef.current = now;
    setOpen((o) => !o);
  };


  const quips = useMemo(() => {
    const key = getRouteKey(location.pathname);
    return ROUTE_QUIPS[key] ?? FALLBACK_QUIPS;
  }, [location.pathname]);

  // Reset tick when route changes so first quip is route-relevant
  useEffect(() => setTick(0), [location.pathname]);

  const message = messages[messages.length - 1] ?? null;
  const quip = quips[tick % quips.length];
  const mood = message?.mood ?? quip.mood;
  const src = MOOD_MAP[mood];
  const unread = messages.length;

  // A fresh message opens the bubble once so notifications are never missed.
  useEffect(() => {
    if (message) setOpen(true);
    setAnswered(null);
  }, [message?.id]);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem("finplay:finfox", next ? "on" : "off");
  };

  if (!enabled) {
    return (
      <button
        onClick={toggleEnabled}
        title="Wake up FinFox"
        aria-label="Wake up FinFox"
        className="fixed bottom-24 right-4 z-40 h-11 w-11 rounded-full bg-background/90 backdrop-blur border border-border shadow-md flex items-center justify-center hover:scale-110 transition overflow-hidden"
      >
        <img src={smartHoodie} alt="" className="h-9 w-9 opacity-70" />
      </button>
    );
  }

  return (
    <>
    <MemeCache open={cacheOpen} onOpenChange={setCacheOpen} />
    <div className="fixed bottom-24 right-4 z-40 flex flex-col items-end gap-2">

      {open && (
        <div className="w-[268px] rounded-2xl rounded-br-sm bg-card border border-border shadow-xl p-3 text-[13px] leading-snug animate-fade-in">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <img src={src} alt="" className="h-7 w-7 rounded-full object-cover bg-muted" draggable={false} />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">FinFox</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {message
                    ? message.kind === "troll"
                      ? "Watching your trades"
                      : message.kind === "question"
                        ? "Quick one for you"
                        : "Update for you"
                    : "Learning sidekick"}
                </p>
              </div>
            </div>
            <button
              onClick={toggleEnabled}
              className="h-7 px-2 rounded-full text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              hide
            </button>
          </div>

          {message ? (
            <div className="rounded-xl bg-muted/50 border border-border/60 px-3 py-2">
              {message.title && (
                <p className="text-[11px] font-semibold text-foreground mb-1">{message.title}</p>
              )}
              <p className="text-foreground text-[12px] leading-relaxed">{message.body}</p>
            </div>
          ) : (
            <p className="text-foreground font-medium rounded-xl bg-muted/50 border border-border/60 px-3 py-2">
              {quip.text}
            </p>
          )}

          {message?.question ? (
            <div className="mt-2.5 space-y-1.5">
              {answered === null ? (
                message.question.options.map((opt, i) => (
                  <button
                    key={opt.label}
                    onClick={() => setAnswered(i)}
                    className="w-full min-h-9 px-3 py-2 rounded-xl border border-border bg-background text-left text-[11px] font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    {opt.label}
                  </button>
                ))
              ) : (
                <>
                  <p
                    className={`text-[11px] font-semibold ${
                      message.question.options[answered]?.correct
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    {message.question.options[answered]?.correct ? "That's it." : "Not quite."}
                  </p>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    {message.question.explanation}
                  </p>
                  <button
                    onClick={() => dismissFinFoxMessage(message.id)}
                    className="mt-1 w-full h-9 rounded-xl bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90 transition-opacity"
                  >
                    Got it
                  </button>
                </>
              )}
            </div>
          ) : message ? (
            <div className="mt-2.5 flex gap-2">
              {message.cta && (
                <button
                  onClick={() => {
                    dismissFinFoxMessage(message.id);
                    navigate(message.cta!.href);
                  }}
                  className="flex-1 h-9 rounded-xl bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90 transition-opacity"
                >
                  {message.cta.label}
                </button>
              )}
              <button
                onClick={() => dismissFinFoxMessage(message.id)}
                className="h-9 px-3 rounded-xl border border-border bg-background text-[11px] font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                Got it
              </button>
            </div>
          ) : (
            <button
              onClick={() => setTick((x) => x + 1)}
              className="mt-2.5 w-full h-9 rounded-xl border border-border bg-background text-[11px] font-medium text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Next tip
            </button>
          )}
        </div>
      )}

      <button
        onClick={handleTap}
        aria-label={
          unread
            ? `FinFox has ${unread} message${unread > 1 ? "s" : ""} for you`
            : "FinFox mascot — tap to open, double-tap for a surprise"
        }
        className={cn(
          "relative h-16 w-16 rounded-full bg-gradient-to-br from-orange-300 to-amber-400 shadow-lg flex items-center justify-center hover:scale-110 transition-transform overflow-hidden ring-2 ring-background select-none",
          unread > 0 && "animate-pulse"
        )}
        title={unread ? "FinFox has something to say" : "Tap to open"}
      >
        <img
          src={src}
          alt="FinFox"
          className="h-full w-full object-cover scale-110 drop-shadow-sm"
          draggable={false}
        />
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background">
            {unread}
          </span>
        ) : (
          <span className="absolute top-0.5 right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-background" />
        )}
      </button>
    </div>
    </>
  );

}


/**
 * Reusable static avatar — for inline placements (cards, headers, empty states).
 * Accepts an `outfit` prop for backward compatibility but always renders the
 * hoodie look for visual consistency.
 */
export function FinFoxAvatar({
  mood = "smart",
  size = 48,
  className,
}: {
  mood?: FoxMood;
  outfit?: "hoodie" | "blazer";
  size?: number;
  className?: string;
}) {
  return (
    <img
      src={MOOD_MAP[mood]}
      alt="FinFox"
      width={size}
      height={size}
      className={cn("rounded-full object-cover bg-gradient-to-br from-orange-100 to-amber-100", className)}
      draggable={false}
    />
  );
}
